import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { CalendarIcon, FireIcon, TrophyIcon } from '@heroicons/react/24/outline';

interface ContributionDay {
  date: string;
  count: number;
  level: 0 | 1 | 2 | 3 | 4;
}

interface GitHubStats {
  totalContributions: number;
  longestStreak: number;
  currentStreak: number;
  bestDay: { date: string; count: number };
}

// Generate realistic contribution data for the past year
const generateContributionData = (): ContributionDay[] => {
  const data: ContributionDay[] = [];
  const today = new Date();
  const oneYearAgo = new Date(today.getFullYear() - 1, today.getMonth(), today.getDate());
  
  for (let d = new Date(oneYearAgo); d <= today; d.setDate(d.getDate() + 1)) {
    const dayOfWeek = d.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    
    // More realistic patterns: higher activity on weekdays, some vacation periods
    let baseChance = isWeekend ? 0.3 : 0.7;
    
    // Add some vacation periods (lower activity)
    const month = d.getMonth();
    if (month === 11 || month === 0) baseChance *= 0.4; // Holiday season
    if (month === 6 || month === 7) baseChance *= 0.6; // Summer vacation
    
    // Add some intense periods (higher activity)
    const weekOfMonth = Math.floor(d.getDate() / 7);
    if (weekOfMonth === 2) baseChance *= 1.3; // Mid-month intensity
    
    const random = Math.random();
    let count = 0;
    let level: 0 | 1 | 2 | 3 | 4 = 0;
    
    if (random < baseChance) {
      if (random < baseChance * 0.1) {
        count = Math.floor(Math.random() * 15) + 10; // 10-24 commits (very active)
        level = 4;
      } else if (random < baseChance * 0.3) {
        count = Math.floor(Math.random() * 8) + 5; // 5-12 commits (active)
        level = 3;
      } else if (random < baseChance * 0.6) {
        count = Math.floor(Math.random() * 4) + 2; // 2-5 commits (moderate)
        level = 2;
      } else {
        count = 1; // 1 commit (light)
        level = 1;
      }
    }
    
    data.push({
      date: d.toISOString().split('T')[0],
      count,
      level
    });
  }
  
  return data;
};

const calculateStats = (data: ContributionDay[]): GitHubStats => {
  const totalContributions = data.reduce((sum, day) => sum + day.count, 0);
  
  let currentStreak = 0;
  let longestStreak = 0;
  let tempStreak = 0;
  
  // Calculate streaks (from most recent)
  for (let i = data.length - 1; i >= 0; i--) {
    if (data[i].count > 0) {
      tempStreak++;
      if (i === data.length - 1 || currentStreak === 0) {
        currentStreak = tempStreak;
      }
    } else {
      longestStreak = Math.max(longestStreak, tempStreak);
      tempStreak = 0;
    }
  }
  longestStreak = Math.max(longestStreak, tempStreak);
  
  // Find best day
  const bestDay = data.reduce((best, day) => 
    day.count > best.count ? day : best
  , { date: '', count: 0 });
  
  return {
    totalContributions,
    longestStreak,
    currentStreak,
    bestDay
  };
};

const getContributionColor = (level: number): string => {
  switch (level) {
    case 0: return 'bg-gray-800';
    case 1: return 'bg-green-900';
    case 2: return 'bg-green-700';
    case 3: return 'bg-green-500';
    case 4: return 'bg-green-300';
    default: return 'bg-gray-800';
  }
};

const getMonthName = (date: string): string => {
  return new Date(date).toLocaleDateString('en-US', { month: 'short' });
};

const getDayOfWeek = (dayIndex: number): string => {
  const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  return days[dayIndex];
};

export default function GitHubActivity() {
  const [contributionData, setContributionData] = useState<ContributionDay[]>([]);
  const [stats, setStats] = useState<GitHubStats | null>(null);
  const [hoveredDay, setHoveredDay] = useState<ContributionDay | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'year' | 'month'>('year');

  useEffect(() => {
    const data = generateContributionData();
    setContributionData(data);
    setStats(calculateStats(data));
  }, []);

  const getDisplayData = () => {
    if (selectedPeriod === 'month') {
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      return contributionData.filter(day => new Date(day.date) >= thirtyDaysAgo);
    }
    return contributionData;
  };

  const displayData = getDisplayData();
  
  // Group data by weeks for proper grid layout
  const weeks: ContributionDay[][] = [];
  let currentWeek: ContributionDay[] = [];
  
  displayData.forEach((day, index) => {
    const dayOfWeek = new Date(day.date).getDay();
    
    if (index === 0) {
      // Fill empty days at the beginning of the first week
      for (let i = 0; i < dayOfWeek; i++) {
        currentWeek.push({ date: '', count: 0, level: 0 });
      }
    }
    
    currentWeek.push(day);
    
    if (dayOfWeek === 6 || index === displayData.length - 1) {
      // End of week or last day
      if (index === displayData.length - 1 && dayOfWeek !== 6) {
        // Fill empty days at the end of the last week
        for (let i = dayOfWeek + 1; i <= 6; i++) {
          currentWeek.push({ date: '', count: 0, level: 0 });
        }
      }
      weeks.push([...currentWeek]);
      currentWeek = [];
    }
  });

  const formatDate = (dateStr: string): string => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (!stats) return null;

  return (
    <section id="github-activity" className="py-20 bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Coding <span className="bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent">Activity</span>
          </h2>
          <p className="text-gray-300 text-lg max-w-3xl mx-auto">
            A visual representation of my coding consistency and dedication. Each square represents a day of development work.
          </p>
          <div className="w-20 h-1 bg-gradient-to-r from-green-300 to-emerald-300 mx-auto rounded-full mt-4"></div>
        </motion.div>

        {/* Stats Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <CalendarIcon className="w-5 h-5 text-green-400" />
              <span className="text-gray-300 text-sm">Total</span>
            </div>
            <div className="text-2xl font-bold text-green-400">
              {stats.totalContributions.toLocaleString()}
            </div>
            <div className="text-gray-400 text-xs">contributions</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <FireIcon className="w-5 h-5 text-orange-400" />
              <span className="text-gray-300 text-sm">Current Streak</span>
            </div>
            <div className="text-2xl font-bold text-orange-400">
              {stats.currentStreak}
            </div>
            <div className="text-gray-400 text-xs">days</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <TrophyIcon className="w-5 h-5 text-yellow-400" />
              <span className="text-gray-300 text-sm">Longest Streak</span>
            </div>
            <div className="text-2xl font-bold text-yellow-400">
              {stats.longestStreak}
            </div>
            <div className="text-gray-400 text-xs">days</div>
          </div>
          
          <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-600">
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-2xl">🚀</span>
              <span className="text-gray-300 text-sm">Best Day</span>
            </div>
            <div className="text-2xl font-bold text-purple-400">
              {stats.bestDay.count}
            </div>
            <div className="text-gray-400 text-xs">commits</div>
          </div>
        </motion.div>

        {/* Period Selector */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex justify-center mb-8"
        >
          <div className="bg-gray-800/50 rounded-lg p-1 border border-gray-600">
            <button
              onClick={() => setSelectedPeriod('year')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                selectedPeriod === 'year'
                  ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Past Year
            </button>
            <button
              onClick={() => setSelectedPeriod('month')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-300 ${
                selectedPeriod === 'month'
                  ? 'bg-green-500/20 text-green-400 border border-green-400/30'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              Past Month
            </button>
          </div>
        </motion.div>

        {/* Contribution Graph */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-gray-800/30 rounded-xl p-6 border border-gray-600 overflow-x-auto"
        >
          <div className="min-w-full">
            {/* Month labels */}
            <div className="flex mb-2">
              <div className="w-8"></div> {/* Space for day labels */}
              <div className="flex-1 flex justify-between text-xs text-gray-400">
                {weeks.map((week, weekIndex) => {
                  if (weekIndex % 4 === 0 && week[0]?.date) {
                    return (
                      <span key={weekIndex} className="text-center">
                        {getMonthName(week[0].date)}
                      </span>
                    );
                  }
                  return <span key={weekIndex}></span>;
                })}
              </div>
            </div>

            {/* Day labels and contribution grid */}
            <div className="flex">
              {/* Day labels */}
              <div className="flex flex-col justify-between text-xs text-gray-400 mr-2">
                {[1, 3, 5].map(dayIndex => (
                  <span key={dayIndex} className="h-3 flex items-center">
                    {getDayOfWeek(dayIndex)}
                  </span>
                ))}
              </div>

              {/* Contribution squares */}
              <div className="flex gap-1">
                {weeks.map((week, weekIndex) => (
                  <div key={weekIndex} className="flex flex-col gap-1">
                    {week.map((day, dayIndex) => (
                      <motion.div
                        key={`${weekIndex}-${dayIndex}`}
                        className={`w-3 h-3 rounded-sm cursor-pointer transition-all duration-200 ${
                          day.date ? getContributionColor(day.level) : 'bg-transparent'
                        } hover:ring-2 hover:ring-green-400/50`}
                        onMouseEnter={() => day.date && setHoveredDay(day)}
                        onMouseLeave={() => setHoveredDay(null)}
                        whileHover={{ scale: 1.2 }}
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: (weekIndex * 7 + dayIndex) * 0.001 }}
                      />
                    ))}
                  </div>
                ))}
              </div>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-between mt-4">
              <div className="text-xs text-gray-400">
                {selectedPeriod === 'year' ? 'Past 12 months' : 'Past 30 days'}
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-xs text-gray-400">Less</span>
                <div className="flex space-x-1">
                  {[0, 1, 2, 3, 4].map(level => (
                    <div
                      key={level}
                      className={`w-3 h-3 rounded-sm ${getContributionColor(level)}`}
                    />
                  ))}
                </div>
                <span className="text-xs text-gray-400">More</span>
              </div>
            </div>
          </div>

          {/* Tooltip */}
          {hoveredDay && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute bg-gray-900 text-white text-xs rounded-lg p-3 border border-gray-600 shadow-xl z-10 pointer-events-none"
              style={{
                transform: 'translate(-50%, -100%)',
                marginTop: '-10px'
              }}
            >
              <div className="font-semibold">
                {hoveredDay.count} contribution{hoveredDay.count !== 1 ? 's' : ''}
              </div>
              <div className="text-gray-400">
                {formatDate(hoveredDay.date)}
              </div>
            </motion.div>
          )}
        </motion.div>

        {/* Fun Facts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-xl p-6 border border-green-400/20">
            <div className="text-2xl mb-2">📈</div>
            <h3 className="text-lg font-semibold text-green-400 mb-2">Consistency</h3>
            <p className="text-gray-300 text-sm">
              Maintained coding activity for {Math.round(stats.totalContributions / 365 * 100)}% of days this year, showing dedication to continuous learning.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-purple-500/10 to-indigo-500/10 rounded-xl p-6 border border-purple-400/20">
            <div className="text-2xl mb-2">⚡</div>
            <h3 className="text-lg font-semibold text-purple-400 mb-2">Peak Performance</h3>
            <p className="text-gray-300 text-sm">
              Best single day had {stats.bestDay.count} commits, demonstrating ability to handle intensive development periods.
            </p>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-xl p-6 border border-orange-400/20">
            <div className="text-2xl mb-2">🔥</div>
            <h3 className="text-lg font-semibold text-orange-400 mb-2">Dedication</h3>
            <p className="text-gray-300 text-sm">
              Longest streak of {stats.longestStreak} consecutive days shows commitment to daily improvement and learning.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}